import React, { useState } from 'react'
import { Box, IconButton, TextField, Tooltip, Typography } from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import SaveIcon from '@mui/icons-material/Save'
import { useSnackbar } from 'notistack'
import { useCorrectMappingMutation } from '../../../redux/growthosBrandsApiSlice'

// Mirrors the real GrowthOS Data->Brands page's tabLabel() exactly, including the
// 'GrowthOS Shipments API' entry already added on the Data page earlier this session.
const tabLabel = (entity) =>
  entity === 'ProShip Shipments' ? 'Shipments tab'
    : entity === 'ProShip Revenue' ? 'Revenue tab'
    : entity === 'GrowthOS Shipments API' ? 'ProShip API'
    : entity

const POSITIVE = '#059669'
const NEGATIVE = '#e11d48'
const INK_500 = '#64748b'

/** One existing mapping's red/green status + editable correction — pixel-matched port of the
 * real app's MappingEditor (border-only status chip, no fill; label beside the input, not
 * above; icon-only save button). mapping: { sourceDealId, hubspotDealId, hubspotMapped,
 * dealName, sourceEntities }. */
export default function MappingEditor({ brandId, mapping }) {
  const { enqueueSnackbar } = useSnackbar()
  const [correctMapping, state] = useCorrectMappingMutation()
  const [value, setValue] = useState(mapping.hubspotDealId || '')
  const [error, setError] = useState('')

  const isMapped = mapping.hubspotMapped
  const sourceTabs = mapping.sourceEntities?.length ? mapping.sourceEntities.map(tabLabel).join(' + ') : 'Manual mapping'
  const changed = value.trim() !== (mapping.hubspotDealId || '')
  const canSave = value.trim() && changed && !state.isLoading

  const save = async () => {
    setError('')
    try {
      await correctMapping({ id: brandId, sourceDealId: mapping.sourceDealId, hubspotDealId: value.trim() }).unwrap()
    } catch (e) {
      const msg = e?.data?.message || e?.message || 'Could not save this mapping.'
      setError(msg)
      enqueueSnackbar(msg, { variant: 'error' })
    }
  }

  return (
    <Box sx={{
      borderRadius: '8px', border: '1px solid', px: 1.5, py: 1,
      borderColor: isMapped ? 'rgba(5,150,105,.25)' : 'rgba(225,29,72,.32)',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
        {isMapped
          ? <CheckCircleOutlineIcon sx={{ fontSize: 13, color: POSITIVE }} />
          : <WarningAmberIcon sx={{ fontSize: 13, color: NEGATIVE }} />}
        <Typography sx={{ fontSize: 11, fontWeight: 600, color: isMapped ? POSITIVE : NEGATIVE }}>
          {isMapped ? 'Exact ID found in HubSpot' : 'No exact ID found in HubSpot'}
        </Typography>
        {isMapped && mapping.dealName && (
          <Typography sx={{ fontSize: 11, color: INK_500 }}>· {mapping.dealName}</Typography>
        )}
      </Box>

      <Typography sx={{ fontSize: 10, lineHeight: '16px', color: INK_500 }}>
        {isMapped
          ? 'This is an exact numeric match only — it is not a recommendation based on the brand name.'
          : 'GrowthOS has not suggested a replacement. Confirm the correct HubSpot Deal ID before saving.'}
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 1.5, rowGap: 0.25, fontSize: 10, color: INK_500, mt: 0.5 }}>
        <Typography component="span" sx={{ fontSize: 10, color: INK_500 }}>
          Found in ProShip: <Box component="span" sx={{ fontWeight: 600 }}>{sourceTabs}</Box>
        </Typography>
        <Typography component="span" sx={{ fontSize: 10, color: INK_500, fontFamily: 'monospace' }}>
          ProShip Deal ID: {mapping.sourceDealId || '—'}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
        <Typography component="label" sx={{ fontSize: 10, fontWeight: 500, color: INK_500, flexShrink: 0 }}>
          HubSpot Deal ID
        </Typography>
        <TextField
          size="small"
          aria-label={`HubSpot Deal ID for ${mapping.sourceDealId}`}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          sx={{ flex: 1, minWidth: 0, '& input': { fontSize: 11, fontFamily: 'monospace', py: 0.4 } }}
        />
        <Tooltip title="Save exact HubSpot Deal ID">
          <span>
            <IconButton size="small" disabled={!canSave} onClick={save} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '10px' }}>
              <SaveIcon sx={{ fontSize: 13 }} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      {error && <Typography sx={{ fontSize: 11, color: NEGATIVE, mt: 0.5 }}>{error}</Typography>}
    </Box>
  )
}
